import { FC, useCallback, useRef } from 'react';
import { CopyIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button, Input } from '@/shared/shadcn';
import { cn, getYandexLang } from '@/shared/lib';
import { Typography } from '@/shared/components';
import { YMaps, Map } from '@pbe/react-yandex-maps';
import { MAP_OPTIONS, ymapsTypes } from '@/entities';
import { useCopyToClipboard, useRTL } from '@/shared/hooks';
import { selectIsMinimalistic, useAppSelector } from '@/app/store';

interface AddressViewContentProps {
  address: string;
  lat: number;
  lng: number;
  areManualCoordinates: boolean;
  onClose: VoidFunction;
  isModal?: boolean;
}

export const AddressViewContent: FC<AddressViewContentProps> = ({
  address,
  lat,
  lng,
  areManualCoordinates,
  onClose,
  isModal,
}) => {
  const isRTL = useRTL();
  const { i18n, t } = useTranslation();
  const handleCopyToClipboard = useCopyToClipboard();
  const isMinimalistic = useAppSelector(selectIsMinimalistic);

  const mapRef = useRef<typeof ymapsTypes.Map | undefined>(undefined);
  const ymapsRef = useRef<typeof ymapsTypes.Map | undefined>(undefined);
  const mapState = { center: [lat, lng], zoom: 15, controls: [] };
  const placemarkRef = useRef<typeof ymapsTypes.Placemark | undefined>(undefined);

  const createPlacemarkOnMap = useCallback(() => {
    if (!ymapsRef.current || !mapRef.current) return;
    placemarkRef.current = new ymapsRef.current.Placemark(
      [lat, lng],
      {
        iconCaption: areManualCoordinates ? `${address} ${lat}, ${lng}` : address,
        balloonContent: areManualCoordinates ? `${address} ${lat}, ${lng}` : address,
      },
      { draggable: false, preset: 'islands#violetDotIconWithCaption' },
    );
    mapRef.current.geoObjects.add(placemarkRef.current);
  }, [address, areManualCoordinates, lat, lng]);

  const onLoadMap = (ymapsInstance: typeof ymapsTypes.Map) => {
    ymapsRef.current = ymapsInstance;
  };

  return (
    <div
      className={cn(
        'gap-3 flex flex-col h-full w-full overflow-auto',
        isModal ? 'p-6' : 'pb-[calc(var(--padding-buttom-footer)-8px)] px-4',
      )}
    >
      <div className={cn('relative mt-4', isModal && 'mt-0')}>
        <Typography
          variant='subtitle3'
          className={cn(
            'absolute top-[-6px] text-muted-foreground bg-background px-[2px] z-[2]',
            isRTL ? 'right-4' : 'left-4',
          )}
        >
          {t('details.address', { ns: 'orders' })}
        </Typography>
        <div className='relative'>
          <Input
            disabled
            value={
              areManualCoordinates ? `${address ? address + ', ' : ''}${lat.toFixed(5)}, ${lng.toFixed(5)}` : address
            }
            className={cn('disabled:opacity-100', isRTL ? 'pl-10' : 'pr-10')}
          />
          <button
            className={cn('p-3 absolute top-0 cursor-pointer', isRTL ? 'left-0' : 'right-0')}
            onClick={() => {
              if (areManualCoordinates)
                handleCopyToClipboard(`${lat}, ${lng}`, t('info.coordsCopied', { ns: 'address' }));
              else handleCopyToClipboard(address, t('info.addressCopied', { ns: 'address' }));
            }}
          >
            <CopyIcon size={16} className='text-muted-foreground' />
          </button>
        </div>
      </div>

      <YMaps
        query={{
          load: 'package.full',
          lang: getYandexLang(i18n.language),
          apikey: import.meta.env.VITE_YANDEX_GEOCODER_API_KEY,
        }}
      >
        <Map
          width='100%'
          height='100%'
          state={mapState}
          onLoad={onLoadMap}
          instanceRef={(ref) => {
            mapRef.current = ref;
            const timer = setTimeout(() => createPlacemarkOnMap(), 100);
            return () => clearTimeout(timer);
          }}
          options={MAP_OPTIONS}
          controls={['zoomControl', 'fullscreenControl']}
          modules={['Placemark', 'geocode', 'geoObject.addon.balloon', 'control.SearchControl']}
          className={cn('w-full h-full overflow-hidden', !isMinimalistic && 'rounded-md')}
        />
      </YMaps>

      <Button size='lg' className='w-full' onClick={onClose}>
        {t('product.done')}
      </Button>
    </div>
  );
};
